import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FileText, Eye, MessageSquare, Users, History, ShieldCheck, Bot, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Logo } from '../../components/Logo';
import { useAuth } from '../../contexts/AuthContext';

const heroSlides = [
  { src: '/screenshots/main_center.png', alt: 'Editor workspace with LaTeX code and live PDF preview', caption: 'Write LaTeX with live PDF preview' },
  { src: '/screenshots/organize_left.png', alt: 'Projects dashboard with folders and organization', caption: 'Organize projects into folders' },
  { src: '/screenshots/verifier_right.png', alt: 'Python verifier running with real-time output', caption: 'Run verifiers to check your work' },
];

export function LandingPage() {
  const { currentUser, loading } = useAuth();

  if (!loading && currentUser) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-8 w-8 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/faq" className="hidden sm:inline text-gray-600 hover:text-gray-900 font-medium">FAQ</Link>
            <Link
              to="/auth"
              className="hidden sm:inline text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign In
            </Link>
            <Link
              to="/auth"
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-primary text-white text-sm sm:text-base font-medium rounded-lg hover:bg-primary-hover transition-colors"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <HeroCarousel />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Tools for Discovery
          </h1>
          <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            A collaborative LaTeX editor with real-time PDF preview, AI-powered writing and chat,
            formal verification with Lean and Python, and tools for publishing open research problems.
          </p>
          <p className="text-sm text-gray-500 mb-8 max-w-2xl mx-auto">
            Private by design: AI assistance runs on a dedicated, independent LLM — your problem sets stay out of training corpora. Perfect for benchmark-ready work.
          </p>
          <p className="text-sm text-gray-500 mb-8 max-w-2xl mx-auto">
            Inspired by <a href="https://prism.openai.com/" target="_blank" rel="noreferrer" className="text-primary hover:underline">OpenAI&apos;s Prism</a>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/auth"
              className="px-6 sm:px-8 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-hover transition-colors text-base sm:text-lg"
            >
              Start Writing
            </Link>
            <a
              href="#features"
              className="px-6 sm:px-8 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-base sm:text-lg"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Everything you need to write and verify research
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Eye className="h-8 w-8" />}
              title="Live PDF Preview"
              description="Compile your LaTeX and see the PDF side by side. The three-panel layout puts your file tree, editor, and preview all in one view."
            />
            <FeatureCard
              icon={<Bot className="h-8 w-8" />}
              title="AI Editing & Chat"
              description="Ask the AI to rewrite sections, fix errors, or write equations — then accept or reject each change individually. Use AI Chat to brainstorm and ask questions without modifying your files."
            />
            <FeatureCard
              icon={<ShieldCheck className="h-8 w-8" />}
              title="Lean & Python Verifiers"
              description="Run Python scripts or check Lean 4 proofs directly from the editor. Build open problems where a verified solution is a genuine discovery."
            />
            <FeatureCard
              icon={<Users className="h-8 w-8" />}
              title="Real-time Collaboration"
              description="Edit simultaneously with collaborators. See live cursors, who is online, and which file each person is working on. Share projects with read, comment, or edit access."
            />
            <FeatureCard
              icon={<MessageSquare className="h-8 w-8" />}
              title="Comments & Review"
              description="Leave line-anchored comments with threaded replies. Submit projects for review and receive feedback with email notifications."
            />
            <FeatureCard
              icon={<History className="h-8 w-8" />}
              title="Revision History"
              description="Every compilation saves a snapshot of your files and PDF. Browse, compare, and restore any previous version with one click."
            />
            <FeatureCard
              icon={<FileText className="h-8 w-8" />}
              title="Full-Featured Editor"
              description="Built on Monaco (the VS Code engine) with LaTeX syntax highlighting, spellcheck, bracket matching, a LaTeX toolbar, and keyboard shortcuts."
            />
            <FeatureCard
              icon={<Download className="h-8 w-8" />}
              title="Download & Export"
              description="Download the compiled PDF or export your entire project as a ZIP archive — including all files, folders, and images."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to start writing?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join researchers, students, and writers using <Logo />.
          </p>
          <Link
            to="/auth"
            className="inline-block px-8 py-3 bg-white text-primary font-medium rounded-lg hover:bg-gray-100 transition-colors text-lg"
          >
            Create Free Account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-gray-400" />
            <Logo className="text-white font-medium" />
          </div>
          <div className="flex gap-6 text-sm">
            <a href="/#features" className="hover:text-white transition-colors">Features</a>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
          </div>
          <div className="text-sm">
            &copy; {new Date().getFullYear()} <Logo />. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent((i) => (i + 1) % heroSlides.length), []);
  const prev = useCallback(() => setCurrent((i) => (i - 1 + heroSlides.length) % heroSlides.length), []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [paused, next]);

  return (
    <div
      className="relative mb-6 sm:mb-8 mx-auto max-w-4xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-lg">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {heroSlides.map((slide) => (
            <img
              key={slide.src}
              src={slide.src}
              alt={slide.alt}
              className="w-full flex-shrink-0 object-cover"
              draggable={false}
            />
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow transition-colors"
      >
        <ChevronLeft className="h-5 w-5 text-gray-700" />
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow transition-colors"
      >
        <ChevronRight className="h-5 w-5 text-gray-700" />
      </button>

      {/* Dots + caption */}
      <div className="mt-3 flex flex-col items-center gap-2">
        <div className="flex gap-2">
          {heroSlides.map((slide, i) => (
            <button
              key={slide.src}
              onClick={() => setCurrent(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                i === current ? 'w-6 bg-primary' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
        <p className="text-sm text-gray-500">{heroSlides[current].caption}</p>
      </div>
    </div>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  comingSoon?: boolean;
}

function FeatureCard({ icon, title, description, comingSoon }: FeatureCardProps) {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
      <div className="text-primary mb-4">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
        {title}
        {comingSoon && (
          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
            Coming Soon
          </span>
        )}
      </h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}
